import { FC, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Typography,
} from '@mui/material';
import { Close, FileCopy, Refresh } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import { t } from 'i18next';
import { theme } from '@store';
import { toolbarFieldSx } from '@ui';
import { ConfirmationModal, TableNoResultPlaceholder } from '@components';
import {
  IDownloadsTableEntry,
  IDownloadsTableFilters,
  useDownloadsTable,
} from '@views/pages/Downloads/hooks';
import { DataGridPro, GridPagination } from '@mui/x-data-grid-pro';
import { ColumnDensityFilter } from 'components/TablePreferences/ColumnDensityFilter';
import {
  TableColumnHeightMapper,
  TableHeaderHeight,
} from 'components/TablePreferences';

interface DownloadsTableProps {
  rows: IDownloadsTableEntry[];
  filters: IDownloadsTableFilters;
  onPageIndexChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => void;
  onPageSizeChange: (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => void;
  onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  setRowsSelection: (selectedIds: Set<string>) => void;
  handleDownload: (row: IDownloadsTableEntry) => void;
  handleCancel: (row: IDownloadsTableEntry) => void;
  handleRetry: (row: IDownloadsTableEntry) => void;
  totalCount: number;
  loading: boolean;
  isDeleting: boolean;
  isMultipleDownloads: boolean;
  currentDownloadItem: IDownloadsTableEntry | null;
}

export const DownloadsTable: FC<DownloadsTableProps> = ({
  rows,
  loading,
  totalCount,
  filters,
  onPageIndexChange,
  onPageSizeChange,
  onSearchChange,
  setRowsSelection,
  handleDownload,
  currentDownloadItem,
  handleCancel,
  handleRetry,
  isDeleting,
  isMultipleDownloads,
}) => {
  const {
    density,
    setDensity,
    resetView,
    columns,
    paginationModel,
    rowSelectionModel,
    handleRowSelectionModelChange,
    handlePaginationModelChange,
  } = useDownloadsTable({
    rows,
    filters,
    onPageIndexChange,
    onPageSizeChange,
    setRowsSelection,
    handleDownload,
    handleCancel,
    handleRetry,
    isDeleting,
    isMultipleDownloads,
    currentDownloadItem,
  });

  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [searchValue, setSearchValue] = useState(filters.search ?? '');

  const handleConfirmReset = () => {
    resetView();
    setIsResetModalOpen(false);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
    onSearchChange(event);
  };

  const handleClearSearch = () => {
    setSearchValue('');
    onSearchChange({
      target: { value: '' },
    } as React.ChangeEvent<HTMLInputElement>);
  };

  const hasSearch = !!filters.search;

  if (rows.length === 0 && !loading && !hasSearch) {
    return (
      <Box
        sx={{
          height: 'calc(100vh - 300px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <FileCopy sx={{ width: 56, height: 56, mb: 2 }} color='primary' />
        <Typography variant='h6'>
          {t('downloads.table.noResults.title')}
        </Typography>
        <Typography variant='body2' sx={{ color: 'text.secondary' }}>
          {t('downloads.table.noResults.subTitle')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        overflowX: 'auto',
      }}
    >
      <Box
        sx={{
          width: '100%',
          padding: 2,
          display: 'flex',
          bgcolor: 'white',
          border: theme => `1px solid ${theme.palette.grey[300]}`,
          borderBottom: 'none',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <OutlinedInput
          id='downloadsSearchField'
          placeholder={t('downloads.table.searchPlaceholder')}
          onChange={handleSearchChange}
          value={searchValue}
          size='medium'
          sx={{
            ...toolbarFieldSx,
            '& input': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
          }}
          startAdornment={
            <InputAdornment sx={{ padding: 0 }} position='start'>
              <SearchIcon />
            </InputAdornment>
          }
          endAdornment={
            searchValue ? (
              <InputAdornment position='end'>
                <IconButton
                  aria-label={t('common.clear')}
                  edge='end'
                  size='small'
                  onClick={handleClearSearch}
                >
                  <Close fontSize='small' />
                </IconButton>
              </InputAdornment>
            ) : null
          }
        />
        <ColumnDensityFilter
          handleDensityChange={setDensity}
          selectedValue={density}
        />
      </Box>
      <DataGridPro
        sx={{
          '--DataGrid-overlayHeight': 'calc(100vh - 360px)',
          border: theme => `1px solid ${theme.palette.grey[300]}`,
          maxHeight: 'calc(100vh - 360px)',
          borderTop: 'none',
          borderRadius: 0,
          '& .MuiDataGrid-columnHeaders': {
            bgcolor: theme.palette.headerBackground.main,
          },
          '& .MuiDataGrid-columnHeader': {
            bgcolor: theme.palette.headerBackground.main,
          },
        }}
        rows={rows}
        columns={columns}
        rowCount={totalCount}
        loading={loading}
        pagination
        paginationMode='server'
        sortingMode='server'
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        pageSizeOptions={[10, 25, 50, 100]}
        checkboxSelection
        disableRowSelectionOnClick
        isRowSelectable={params =>
          params.row.status === 'completed' || params.row.status === 'failed'
        }
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={handleRowSelectionModelChange}
        disableColumnFilter
        disableColumnSelector
        disableColumnReorder
        disableMultipleColumnsSorting
        columnHeaderHeight={TableHeaderHeight}
        getRowHeight={() => TableColumnHeightMapper[density]}
        getRowClassName={params =>
          params.indexRelativeToCurrentPage % 2 !== 0 ? 'row-even' : ''
        }
        slots={{
          pagination: props => (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
              }}
            >
              <Button
                color='primary'
                sx={{ whiteSpace: 'nowrap' }}
                startIcon={<Refresh color='primary' />}
                onClick={() => setIsResetModalOpen(true)}
              >
                {t('downloads.table.resetView')}
              </Button>
              <GridPagination {...props} />
            </Box>
          ),
          noRowsOverlay: () => (
            <Box
              sx={{
                display: 'grid',
                placeItems: 'center',
                width: '100%',
                height: '100%',
                bgcolor: theme.palette.common.white,
              }}
            >
              <TableNoResultPlaceholder />
            </Box>
          ),
        }}
      />
      <ConfirmationModal
        title={t('downloads.resetViewModal.title')}
        message={t('downloads.resetViewModal.message')}
        closeText={t('downloads.resetViewModal.keep')}
        confirmText={t('downloads.resetViewModal.confirm')}
        onConfirm={handleConfirmReset}
        onClose={() => setIsResetModalOpen(false)}
        isOpen={isResetModalOpen}
        disableCloseOnOutsideClick={false}
        reverseButtonOrder
      />
    </Box>
  );
};
